import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Button from '../components/ui/Button';
import Icon from '../components/ui/Icon';
import useScrollAnimation from '../hooks/useScrollAnimation';
import logo from '../assets/valendris-logo.png';
import './Home.scss';

const services = [
  {
    icon: 'code',
    title: 'Custom SaaS Development',
    description: 'SaaS products that scale with your business and don\'t need constant firefighting.',
  },
  {
    icon: 'smartphone',
    title: 'Mobile App Development',
    description: 'iOS and Android apps that users actually want to open—fast, reliable, and intuitive.',
  },
  {
    icon: 'link',
    title: 'Integrations & Automations',
    description: 'Connect your systems and eliminate the manual copy-paste work slowing your team down.',
  },
  {
    icon: 'zap',
    title: 'MVPs & Prototypes',
    description: 'Test your idea with real users in weeks, not months. Built for speed and iteration.',
  },
  {
    icon: 'refresh',
    title: 'Modernization & Rebuilds',
    description: 'Replace the legacy system everyone\'s afraid to touch with something maintainable.',
  },
  {
    icon: 'settings',
    title: 'Ongoing Maintenance',
    description: 'Keep your software running smoothly so you can focus on your business, not bugs.',
  },
];

const whyUs = [
  {
    icon: 'shield',
    title: 'Secure by Default',
    description: 'Secrets in managed vaults. Least-privilege access. Audit logs and change tracking on every project.',
  },
  {
    icon: 'messageCircle',
    title: 'Direct Communication',
    description: 'No account managers. No handoffs. You talk directly to the engineer building your software.',
  },
  {
    icon: 'target',
    title: 'Milestone Delivery',
    description: 'Work is delivered in clear milestones with demos at each stage. Pay as value is delivered.',
  },
];

const trustSignals = [
  { icon: 'checkCircle', text: 'Minnesota, USA (Central Time)' },
  { icon: 'shield', text: 'NDA-friendly' },
  { icon: 'clock', text: 'Response within 1 business day' },
  { icon: 'target', text: 'Milestone-based billing' },
];

const techStack = [
  { name: 'React', category: 'frontend' },
  { name: 'TypeScript', category: 'language' },
  { name: 'Node.js', category: 'backend' },
  { name: 'C#', category: 'language' },
  { name: '.NET', category: 'backend' },
  { name: 'Java', category: 'language' },
  { name: 'Rust', category: 'language' },
  { name: 'PostgreSQL', category: 'database' },
  { name: 'AWS', category: 'cloud' },
  { name: 'Electron', category: 'desktop' },
  { name: 'React Native', category: 'mobile' },
  { name: 'Docker', category: 'devops' },
];

function Home() {
  const [heroVisible, setHeroVisible] = useState(false);
  const [servicesRef, servicesVisible] = useScrollAnimation<HTMLElement>();
  const [whyUsRef, whyUsVisible] = useScrollAnimation<HTMLElement>();
  const [techRef, techVisible] = useScrollAnimation<HTMLElement>();
  const [aboutRef, aboutVisible] = useScrollAnimation<HTMLElement>();
  const [trustRef, trustVisible] = useScrollAnimation<HTMLElement>();
  const [ctaRef, ctaVisible] = useScrollAnimation<HTMLElement>();

  useEffect(() => {
    const timer = setTimeout(() => setHeroVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <Helmet>
        <title>Valendris | Custom Software Development</title>
        <meta name="description" content="Custom SaaS, mobile apps, and internal tools built with clean code and clear communication. Milestone-based delivery from a solo developer with 10 years of experience." />
      </Helmet>
      <div className="home">
        {/* Hero Section */}
        <section className="home-hero">
          <div className="home-hero-bg">
            <div className="home-hero-gradient" />
            <div className="home-hero-glow" />
          </div>
          <div className="home-hero-container container">
            <div className={`home-hero-content ${heroVisible ? 'visible' : ''}`}>
              <img src={logo} alt="Valendris" className="home-hero-logo" />
              <h1 className="home-hero-title">
                Software that ships fast<br />and stays maintainable.
              </h1>
              <p className="home-hero-slogan">
                Milestone-based delivery. Clean code. Direct communication.
              </p>
              <p className="home-hero-description">
                I build SaaS products, mobile apps, and internal tools—scoped clearly,
                delivered in milestones, and supported after launch.
              </p>
              <div className="home-hero-actions">
                <Button to="/contact">Start a Project</Button>
                <Button to="/how-we-work" variant="secondary">How I Work</Button>
              </div>
            </div>
          </div>
          <div className="home-hero-scroll">
            <span className="home-hero-scroll-text">Scroll</span>
            <div className="home-hero-scroll-line" />
          </div>
        </section>

        {/* Services Section */}
        <section ref={servicesRef} className={`home-services ${servicesVisible ? 'visible' : ''}`}>
          <div className="home-services-container container">
            <header className="home-services-header">
              <h2 className="home-services-title">What I Build</h2>
              <p className="home-services-subtitle">
                From initial concept to production-ready software
              </p>
            </header>
            <div className="home-services-grid">
              {services.map((service, index) => (
                <div
                  key={service.title}
                  className="home-services-card"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="home-services-card-icon">
                    <Icon name={service.icon} size={28} />
                  </div>
                  <h3 className="home-services-card-title">{service.title}</h3>
                  <p className="home-services-card-description">{service.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Us Section */}
        <section ref={whyUsRef} className={`home-why ${whyUsVisible ? 'visible' : ''}`}>
          <div className="home-why-container container">
            <header className="home-why-header">
              <h2 className="home-why-title">Why Work With Me</h2>
              <p className="home-why-subtitle">
                The advantages of working with a solo expert
              </p>
            </header>
            <div className="home-why-grid">
              {whyUs.map((item, index) => (
                <div
                  key={item.title}
                  className="home-why-card"
                  style={{ transitionDelay: `${index * 0.15}s` }}
                >
                  <div className="home-why-card-icon">
                    <Icon name={item.icon} size={32} />
                  </div>
                  <h3 className="home-why-card-title">{item.title}</h3>
                  <p className="home-why-card-description">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Tech Stack Section */}
        <section ref={techRef} className={`home-tech ${techVisible ? 'visible' : ''}`}>
          <div className="home-tech-container container">
            <header className="home-tech-header">
              <h2 className="home-tech-title">Technologies I Work With</h2>
              <p className="home-tech-subtitle">
                10 years across frontend, backend, mobile, and desktop
              </p>
            </header>
            <div className="home-tech-grid">
              {techStack.map((tech, index) => (
                <div
                  key={tech.name}
                  className="home-tech-item"
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  {tech.name}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* About Section */}
        <section ref={aboutRef} className={`home-about ${aboutVisible ? 'visible' : ''}`}>
          <div className="home-about-container container">
            <div className="home-about-content">
              <h2 className="home-about-title">About</h2>
              <p className="home-about-text">
                Valendris is a solo software development practice with 10 years of experience
                building web applications, mobile apps, and SaaS products. I work directly with
                clients—no account managers, no handoffs, no telephone game.
              </p>
              <p className="home-about-text">
                References available upon request. Happy to work under NDA.
              </p>
            </div>
          </div>
        </section>

        {/* Trust Signals */}
        <section ref={trustRef} className={`home-trust ${trustVisible ? 'visible' : ''}`}>
          <div className="home-trust-container container">
            <ul className="home-trust-list">
              {trustSignals.map((signal, index) => (
                <li
                  key={signal.text}
                  className="home-trust-item"
                  style={{ transitionDelay: `${index * 0.1}s` }}
                >
                  <Icon name={signal.icon} size={20} className="home-trust-item-icon" />
                  <span>{signal.text}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* CTA Section */}
        <section ref={ctaRef} className={`home-cta ${ctaVisible ? 'visible' : ''}`}>
          <div className="home-cta-container container">
            <div className="home-cta-content">
              <h2 className="home-cta-title">Ready to build something great?</h2>
              <p className="home-cta-description">
                Tell me about your project and I'll get back to you within 1 business day.
              </p>
              <Button to="/contact">Get in Touch</Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}

export default Home;
